import React, { useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { CompleteCheckbox, EditableText, MoveTriggerIcon } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import { TemplateSubtaskDueOnForm } from "~/pages/ProjectTemplates/Tasks/Subtasks/TemplateSubtaskDueOnForm";
import { EDIT_PROJECT_TEMPLATE_ELEMENT_ERROR } from "~/store/action_types";
import { editProjectTemplateElement } from "~/store/actions/actions.projectTemplateElements";
import { TemplateSubtaskAssigneePicker } from "./TemplateSubtaskAssigneePicker";
import { TemplateSubtaskDelete } from "./TemplateSubtaskDelete";

export const TemplateSubtaskItem = ({ subtask, isHiddenFromClients, isPreview, isDragging, shouldShowDragHandler }) => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);

  const handleChange = useCallback(
    (attr) => {
      dispatch(editProjectTemplateElement(subtask.id, subtask.template_id, { ...subtask, ...attr })).then(
        (response) => {
          if (response.type === EDIT_PROJECT_TEMPLATE_ELEMENT_ERROR) {
            inputRef.current.value = subtask.name;
          }
        }
      );
    },
    [dispatch, subtask]
  );

  const handleRename = (e) => {
    const name = e.target.value.trim();
    if (subtask.name?.trim() !== name) {
      handleChange({ name: e.target.value, body: e.target.value });
    }
  };

  return (
    <div className="tw-relative tw-group task-list-item tw-flex tw-items-center tw-w-full tw-bg-page-paper-main">
      <div
        className={classNames(
          "task-item-drag-handler tw-flex tw-items-center tw-h-full tw-w-7 tw-justify-center tw-opacity-0 group-hover:tw-opacity-100 tw-shrink-0",
          {
            "tw-opacity-100": isPreview,
          }
        )}
      >
        {shouldShowDragHandler ? <MoveTriggerIcon className="drag-icon tw-ml-[5px] tw-cursor-move" /> : null}
      </div>
      <div
        className={classNames(
          "tw-grid tw-grid-cols-[1fr_auto] tw-items-center tw-h-10 group-hover:tw-bg-theme-200 tw-rounded-lg tw-px-1.5 tw-w-full tw-gap-2",
          { "tw-bg-theme-200": isDragging },
          { "hover:tw-bg-theme-200": !isDragging && !isPreview },
          { "tw-bg-page-paper-main tw-shadow-md": isPreview }
        )}
      >
        <div
          className="tw-flex tw-items-center tw-gap-2 tw-min-w-0"
          style={{ opacity: isDragging ? 0 : 1 }}
        >
          <CompleteCheckbox
            disabled
            className="tw-shrink-0"
          />
          <div className="tw-min-w-0 tw-overflow-hidden">
            <EditableText
              value={subtask.name}
              variant="Body 2"
              weight="regular"
              onSave={handleRename}
              inputProps={{ maxLength: 150 }}
              className="tw-truncate tw-w-full tw-block"
              ref={inputRef}
              onCancel={(e) => e.stopPropagation()}
            />
          </div>
        </div>
        <div
          className={classNames("tw-flex tw-items-center tw-justify-center tw-gap-2", {
            "tw-opacity-0": isDragging && !isPreview,
          })}
        >
          <div
            className={classNames("group-hover:tw-opacity-100", {
              "tw-opacity-100": subtask.due_on > 0 && !isDragging,
            })}
          >
            <TemplateSubtaskDueOnForm
              dueOn={subtask.due_on}
              onChange={handleChange}
            />
          </div>
          <div
            className={classNames("group-hover:tw-opacity-100", {
              "tw-opacity-100": subtask.assignee_id > 0 && !isDragging,
            })}
          >
            <TemplateSubtaskAssigneePicker
              subtask={subtask}
              isHiddenFromClients={isHiddenFromClients}
              onChange={handleChange}
              className={classNames({
                "show-on-hover": !subtask.assignee_id,
              })}
            />
          </div>
          <div className="tw-opacity-0 group-hover:tw-opacity-100">
            <TemplateSubtaskDelete subtask={subtask} />
          </div>
        </div>
      </div>
    </div>
  );
};

TemplateSubtaskItem.propTypes = {
  subtask: PropTypes.object.isRequired,
  isHiddenFromClients: PropTypes.bool,
  isPreview: PropTypes.bool,
  isDragging: PropTypes.bool,
  shouldShowDragHandler: PropTypes.bool,
};
